import { useRef, useState } from "react";
import { useLocation } from "wouter";
import { FileText, Loader2, Plus, Sparkles, Trash2, Upload } from "lucide-react";
import {
  useParseInvoicePdf,
  useCommitInvoicePdf,
  useCommitEstimatePdf,
  type ParsedPdfInvoice,
  type CommitPdfInvoiceBody,
  type CommitPdfEstimateBody,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { InfoTip } from "@/components/ui/info-tip";
import { FORMULAS } from "@/lib/formulas";
import { cn } from "@/lib/utils";

const MAX_MB = 15;

type DocType = "invoice" | "estimate";

type Editable = {
  docType: DocType;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerAddress: string;
  docNumber: string;
  docDate: string;
  secondaryDate: string; // dueDate for invoice, validUntil for estimate
  notes: string;
  discountType: "percent" | "fixed" | "none";
  discountValue: string;
  taxRate: string;
  // invoice-only
  invoiceStatus: "draft" | "sent" | "paid" | "partial" | "overdue" | "void";
  amountPaid: string;
  // estimate-only
  estimateStatus: "draft" | "sent" | "accepted" | "declined" | "converted";
  lineItems: { description: string; quantity: string; unitPrice: string }[];
};

function guessDocType(p: ParsedPdfInvoice): DocType {
  if (p.documentType === "quote") return "estimate";
  return "invoice";
}

function toEditable(p: ParsedPdfInvoice): Editable {
  return {
    docType: guessDocType(p),
    customerName: p.customer.name ?? "",
    customerEmail: p.customer.email ?? "",
    customerPhone: p.customer.phone ?? "",
    customerAddress: p.customer.address ?? "",
    docNumber: p.invoiceNumber ?? "",
    docDate: p.invoiceDate ?? "",
    secondaryDate: p.dueDate ?? "",
    notes: p.notes ?? "",
    discountType: p.discountType ?? "none",
    discountValue: p.discountValue != null ? String(p.discountValue) : "",
    taxRate: p.taxRate != null ? String(p.taxRate) : "",
    invoiceStatus: p.status && ["draft","sent","paid","partial","overdue","void"].includes(p.status as string)
      ? (p.status as Editable["invoiceStatus"])
      : "draft",
    amountPaid: p.amountPaid != null ? String(p.amountPaid) : "",
    estimateStatus: "draft",
    lineItems: (p.lineItems.length ? p.lineItems : [{ description: "", quantity: 1, unitPrice: 0 }]).map((li) => ({
      description: li.description,
      quantity: String(li.quantity),
      unitPrice: String(li.unitPrice),
    })),
  };
}

function num(v: string): number {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

export function PdfInvoiceImport() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const parseMut = useParseInvoicePdf();
  const commitInvoiceMut = useCommitInvoicePdf();
  const commitEstimateMut = useCommitEstimatePdf();
  const [form, setForm] = useState<Editable | null>(null);
  const [fileName, setFileName] = useState("");

  const handleFile = async (file: File) => {
    if (file.type !== "application/pdf") {
      toast({ title: "PDF required", description: "Please choose a PDF file.", variant: "destructive" });
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast({ title: "File too large", description: `Please choose a PDF under ${MAX_MB} MB.`, variant: "destructive" });
      return;
    }
    setFileName(file.name);
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
    const base64 = dataUrl.includes(",") ? dataUrl.slice(dataUrl.indexOf(",") + 1) : dataUrl;
    try {
      const res = await parseMut.mutateAsync(base64);
      setForm(toEditable(res.invoice));
      toast({ title: "PDF read", description: "Review the details below, then create the document." });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Couldn't read this PDF.";
      toast({ title: "Couldn't read PDF", description: message, variant: "destructive" });
      setFileName("");
    }
  };

  const subtotal = form ? form.lineItems.reduce((s, li) => s + num(li.quantity) * num(li.unitPrice), 0) : 0;
  const discountAmount = (() => {
    if (!form || form.discountType === "none") return 0;
    const v = num(form.discountValue);
    const raw = form.discountType === "percent" ? subtotal * (v / 100) : v;
    return Math.max(0, Math.min(raw, subtotal));
  })();
  const taxableBase = subtotal - discountAmount;
  const taxAmount = form ? taxableBase * (num(form.taxRate) / 100) : 0;
  const total = taxableBase + taxAmount;

  const update = (patch: Partial<Editable>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const updateItem = (i: number, patch: Partial<Editable["lineItems"][number]>) =>
    setForm((f) => (f ? { ...f, lineItems: f.lineItems.map((li, idx) => (idx === i ? { ...li, ...patch } : li)) } : f));

  const reset = () => {
    setForm(null);
    setFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const isPending = commitInvoiceMut.isPending || commitEstimateMut.isPending;

  const handleCommit = async () => {
    if (!form) return;
    if (!form.customerName.trim()) {
      toast({ title: "Customer name required", variant: "destructive" });
      return;
    }
    const lineItems = form.lineItems
      .filter((li) => li.description.trim())
      .map((li) => ({ description: li.description.trim(), quantity: num(li.quantity), unitPrice: num(li.unitPrice) }));
    if (lineItems.length === 0) {
      toast({ title: "At least one line item is required", variant: "destructive" });
      return;
    }

    const customer = {
      name: form.customerName.trim(),
      email: form.customerEmail.trim() || null,
      phone: form.customerPhone.trim() || null,
      address: form.customerAddress.trim() || null,
    };
    const sharedFields = {
      customer,
      notes: form.notes.trim() || null,
      discountType: form.discountType === "none" ? null : form.discountType,
      discountValue: form.discountType === "none" ? null : num(form.discountValue),
      taxRate: form.taxRate.trim() ? num(form.taxRate) : null,
      lineItems,
    };

    try {
      if (form.docType === "invoice") {
        const body: CommitPdfInvoiceBody = {
          ...sharedFields,
          invoiceNumber: form.docNumber.trim() || null,
          invoiceDate: form.docDate.trim() || null,
          dueDate: form.secondaryDate.trim() || null,
          status: form.invoiceStatus,
          amountPaid: form.amountPaid.trim() ? num(form.amountPaid) : null,
        };
        const res = await commitInvoiceMut.mutateAsync(body);
        toast({
          title: "Invoice created",
          description: `${res.invoiceNumber}${res.customerCreated ? " · new customer added" : ""}`,
        });
        reset();
        navigate(`/invoices/${res.invoiceId}`);
      } else {
        const body: CommitPdfEstimateBody = {
          ...sharedFields,
          estimateNumber: form.docNumber.trim() || null,
          estimateDate: form.docDate.trim() || null,
          validUntil: form.secondaryDate.trim() || null,
          status: form.estimateStatus,
        };
        const res = await commitEstimateMut.mutateAsync(body);
        toast({
          title: "Quote created",
          description: `${res.estimateNumber ?? ""}${res.customerCreated ? " · new customer added" : ""}`,
        });
        reset();
        navigate(`/estimates/${res.estimateId}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create the document.";
      toast({ title: "Couldn't create document", description: message, variant: "destructive" });
    }
  };

  return (
    <Card className="border-primary/20">
      <CardContent className="pt-6 space-y-5">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-primary/10 p-2">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-base">Import from PDF</h3>
            <p className="text-sm text-muted-foreground">
              Upload an existing invoice or quote PDF and we'll read it and pre-fill a new document for you to review. Great for bringing past jobs into your metrics.
            </p>
          </div>
        </div>

        {!form && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFile(file);
              }}
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={parseMut.isPending}
              className="gap-2"
            >
              {parseMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {parseMut.isPending ? "Reading PDF…" : "Choose PDF"}
            </Button>
            <p className="text-xs text-muted-foreground mt-2">PDF up to {MAX_MB} MB. Digital or scanned.</p>
          </div>
        )}

        {form && (
          <div className="space-y-5">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="w-4 h-4" />
              <span className="truncate">{fileName}</span>
              <Button variant="ghost" size="sm" className="ml-auto h-7" onClick={reset}>Start over</Button>
            </div>

            {/* Document type toggle */}
            <div className="flex gap-2 p-1 bg-border rounded-lg w-fit">
              {(["invoice", "estimate"] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => update({ docType: t })}
                  className={cn(
                    "px-4 py-1.5 rounded-md text-sm font-medium transition-colors",
                    form.docType === t
                      ? "bg-card shadow text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {t === "invoice" ? "Invoice" : "Quote"}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Customer name"><Input value={form.customerName} onChange={(e) => update({ customerName: e.target.value })} /></Field>
              <Field label="Customer email"><Input value={form.customerEmail} onChange={(e) => update({ customerEmail: e.target.value })} /></Field>
              <Field label="Phone"><Input value={form.customerPhone} onChange={(e) => update({ customerPhone: e.target.value })} /></Field>
              <Field label="Address"><Input value={form.customerAddress} onChange={(e) => update({ customerAddress: e.target.value })} /></Field>
              <Field label={form.docType === "invoice" ? "Invoice #" : "Quote #"}>
                <Input value={form.docNumber} onChange={(e) => update({ docNumber: e.target.value })} placeholder="Auto-generate if blank" />
              </Field>
              <Field label="Status">
                {form.docType === "invoice" ? (
                  <Select value={form.invoiceStatus} onValueChange={(v) => update({ invoiceStatus: v as Editable["invoiceStatus"] })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(["draft", "sent", "paid", "partial", "overdue", "void"] as const).map((s) => (
                        <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Select value={form.estimateStatus} onValueChange={(v) => update({ estimateStatus: v as Editable["estimateStatus"] })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(["draft", "sent", "accepted", "declined", "converted"] as const).map((s) => (
                        <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label={form.docType === "invoice" ? "Invoice date" : "Quote date"}>
                <Input type="date" value={form.docDate} onChange={(e) => update({ docDate: e.target.value })} />
              </Field>
              <Field label={form.docType === "invoice" ? "Due date" : "Valid until"}>
                <Input type="date" value={form.secondaryDate} onChange={(e) => update({ secondaryDate: e.target.value })} />
              </Field>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Line items</Label>
              {form.lineItems.map((li, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input className="flex-1" placeholder="Description" value={li.description} onChange={(e) => updateItem(i, { description: e.target.value })} />
                  <Input className="w-20" type="number" placeholder="Qty" value={li.quantity} onChange={(e) => updateItem(i, { quantity: e.target.value })} />
                  <Input className="w-28" type="number" placeholder="Unit price" value={li.unitPrice} onChange={(e) => updateItem(i, { unitPrice: e.target.value })} />
                  <Button
                    variant="ghost" size="icon" className="shrink-0 text-destructive"
                    onClick={() => update({ lineItems: form.lineItems.filter((_, idx) => idx !== i) })}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" className="gap-1" onClick={() => update({ lineItems: [...form.lineItems, { description: "", quantity: "1", unitPrice: "0" }] })}>
                <Plus className="w-3.5 h-3.5" /> Add line
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Discount">
                <div className="flex gap-2">
                  <Select value={form.discountType} onValueChange={(v) => update({ discountType: v as Editable["discountType"] })}>
                    <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="percent">Percent</SelectItem>
                      <SelectItem value="fixed">Fixed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="number" disabled={form.discountType === "none"} value={form.discountValue} onChange={(e) => update({ discountValue: e.target.value })} />
                </div>
              </Field>
              <Field label={<span className="inline-flex items-center gap-1">Tax rate % <InfoTip content={FORMULAS.tax} /></span>}>
                <Input type="number" value={form.taxRate} onChange={(e) => update({ taxRate: e.target.value })} />
              </Field>
              {form.docType === "invoice" && (
                <Field label="Amount already paid">
                  <Input type="number" value={form.amountPaid} onChange={(e) => update({ amountPaid: e.target.value })} />
                </Field>
              )}
            </div>

            <Field label="Notes"><Input value={form.notes} onChange={(e) => update({ notes: e.target.value })} /></Field>

            <div className="rounded-md border bg-muted/20 px-4 py-3 text-sm space-y-1">
              <Row label="Subtotal" value={subtotal} />
              {discountAmount > 0 && <Row label="Discount" value={-discountAmount} />}
              {taxAmount > 0 && <Row label="Tax" value={taxAmount} />}
              <div className="flex justify-between font-semibold pt-1 border-t">
                <span className="inline-flex items-center gap-1">Total <InfoTip content={FORMULAS.total} /></span>
                <span>{total.toFixed(2)}</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button onClick={handleCommit} disabled={isPending} className="gap-2">
                {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {form.docType === "invoice" ? "Create invoice" : "Create quote"}
              </Button>
              <Button variant="ghost" onClick={reset} disabled={isPending}>Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex justify-between text-muted-foreground">
      <span>{label}</span>
      <span>{value.toFixed(2)}</span>
    </div>
  );
}
